import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Target } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colour mapping matches the ScoreRing intent badge so the UI feels
// consistent across the app.
const INTENT_META = {
  initial_sales: {
    label: 'Initial Sales',
    tone: 'sky',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-800',
    bar: 'bg-sky-500',
  },
  follow_up: {
    label: 'Follow-Up',
    tone: 'amber',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    bar: 'bg-amber-500',
  },
  non_sales: {
    label: 'Non-Sales',
    tone: 'slate',
    bg: 'bg-slate-50',
    border: 'border-slate-300',
    text: 'text-slate-800',
    bar: 'bg-slate-500',
  },
  unclassified: {
    label: 'Unclassified',
    tone: 'zinc',
    bg: 'bg-zinc-50',
    border: 'border-zinc-200',
    text: 'text-zinc-600',
    bar: 'bg-zinc-300',
  },
};

const scoreTone = (avg) => {
  if (avg >= 75) return 'text-emerald-600';
  if (avg >= 60) return 'text-amber-600';
  return 'text-rose-600';
};

/**
 * Intent Analytics Panel — breaks down an agent's (or a whole domain's)
 * performance by classified call_intent. Managers use this to see e.g.
 * "great on non-sales admin, weak on follow-up" at a glance.
 *
 * Props:
 *   - agentId (optional): restrict to this agent's calls. Omit for
 *     domain-wide roll-up.
 *   - domainId (optional): master-admin override. Tenants are auto-scoped.
 *   - days (default 30): rolling window.
 */
export const IntentBreakdownCard = ({ agentId = null, domainId = null, days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip the API call when neither scope is set — the empty state
    // message will nudge the user to pick an agent instead of firing a
    // pointless request.
    if (!agentId && !domainId) {
      setData({ rows: [], total: 0, days });
      setLoading(false);
      return;
    }
    setLoading(true);
    const p = new URLSearchParams();
    if (agentId) p.set('agent_id', String(agentId));
    if (domainId) p.set('domain_id', String(domainId));
    p.set('days', String(days));
    axios
      .get(`${API}/analytics/intent-breakdown?${p.toString()}`, { timeout: 30000 })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [agentId, domainId, days]);

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm" data-testid="intent-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="w-4 h-4 text-slate-400" />
            Performance by Call Intent
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse h-32 bg-slate-100 rounded" />
        </CardContent>
      </Card>
    );
  }

  const rows = (data?.rows || []).filter((r) => r.count > 0);
  const total = data?.total || 0;
  const hasClassified = rows.some((r) => r.intent !== 'unclassified');

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="intent-breakdown-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="w-4 h-4 text-teal-500" />
          Performance by Call Intent
          <span className="ml-auto text-[11px] font-medium text-slate-500">
            Last {days} days · {total} call{total === 1 ? '' : 's'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 && (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="intent-breakdown-empty">
            {!agentId && !domainId
              ? 'Pick an agent (or a domain) to see the intent breakdown.'
              : 'No graded calls in the window.'}
          </p>
        )}
        {rows.length > 0 && !hasClassified && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
            Every call is <span className="font-semibold">unclassified</span> — run
            the &ldquo;Backfill call intent&rdquo; admin job to populate historical rows.
          </p>
        )}
        <div className="space-y-3">
          {rows.map((r) => {
            const meta = INTENT_META[r.intent] || INTENT_META.unclassified;
            const pct = total ? Math.round((r.count / total) * 100) : 0;
            return (
              <div
                key={r.intent}
                className={`rounded-lg border ${meta.border} ${meta.bg} p-3`}
                data-testid={`intent-row-${r.intent}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${meta.text}`}>
                    {meta.label}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {r.count} call{r.count === 1 ? '' : 's'} · {pct}% of volume
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <p className="text-slate-500 text-[10px] uppercase tracking-wide">Avg Score</p>
                    <p className={`text-lg font-bold ${scoreTone(r.avg_score)}`}>
                      {r.avg_score}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-[10px] uppercase tracking-wide">Pass Rate</p>
                    <p className="text-lg font-bold text-slate-800">
                      {Math.round((r.pass_rate || 0) * 100)}%
                    </p>
                    <p className="text-[10px] text-slate-400">{r.escalated_count} escalated</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-[10px] uppercase tracking-wide">Wins</p>
                    <p className="text-lg font-bold text-emerald-700">{r.won_count}</p>
                  </div>
                </div>
                {/* Volume bar */}
                <div className="mt-2 h-1.5 rounded-full bg-white/80 overflow-hidden">
                  <div
                    className={`h-full ${meta.bar}`}
                    style={{ width: `${Math.max(4, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default IntentBreakdownCard;
